(() => {
  if (customElements.get('ai-pbn-accessories-slider')) return;
  class Accessories extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.rail = this.querySelector('[data-pbn-accessories-rail]');
      this.cards = [...this.querySelectorAll('[data-pbn-accessory-card]')];
      this.controls = this.querySelector('[data-pbn-accessories-controls]');
      if (!this.rail) return;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      if (this.controls) this.controls.hidden = true;
      this.rail.tabIndex = 0;
      this.rail.setAttribute('aria-label', 'Frequently bought together accessories');
      this.setAttribute('data-pbn-accessories-ready', '');
      this.observeCartState();
    }
    disconnectedCallback() {
      this.unsubscribeCart?.(); this.unsubscribeCart = null;
      this.abortController?.abort(); this.abortController = null;
      this.pendingAccessoryControls?.forEach(({ spinner }, control) => { control.removeAttribute('aria-busy'); spinner.remove(); });
      this.pendingAccessoryControls?.clear();
      this.cartDrawer = null; this.confirmedAccessoryCart = null;
    }
    observeCartState() {
      const controller = this.abortController;
      this.cartRevision = 0;
      this.cartPending = false;
      this.pendingAccessoryControls = new Map();
      this.accessoryRead = null;
      this.cartStatus = this.querySelector('[data-pdp-accessories-cart-status]');
      this.rail.addEventListener('click', (event) => this.onAccessoryCartClick(event), { signal: controller.signal });
      customElements.whenDefined('cart-drawer').then(() => {
        if (this.abortController !== controller || controller.signal.aborted) return;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.cartDrawer?.connectProductRail(this.rail, controller.signal);
        if (typeof this.cartDrawer?.prepareProductAdd !== 'function') return;
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (this.abortController !== controller || controller.signal.aborted) return;
            if (Array.isArray(event?.cartData?.items)) {
              this.cartRevision += 1;
              this.syncAccessoryCart(event.cartData);
            } else {
              this.readAccessoryCart(controller);
            }
          });
        }
        window.addEventListener('pageshow', () => this.readAccessoryCart(controller), { signal: controller.signal });
        window.addEventListener('focus', () => this.readAccessoryCart(controller), { signal: controller.signal });
        this.readAccessoryCart(controller);
      });
    }

    async readAccessoryCart(controller) {
      if (!this.cartDrawer || this.cartPending || this.cartDrawer.pendingMutations) return;
      if (this.accessoryRead?.controller === controller) return this.accessoryRead.promise;
      const revision = ++this.cartRevision;
      try {
        const promise = this.cartDrawer.getCart();
        this.accessoryRead = { controller, promise };
        const cart = await promise;
        if (this.abortController !== controller || controller.signal.aborted || revision !== this.cartRevision) return;
        this.syncAccessoryCart(cart);
      } catch {
        if (this.abortController === controller && !controller.signal.aborted && revision === this.cartRevision) {
          this.reportAccessoryCart('The cart could not be checked. Open your cart and try again.', true);
        }
      } finally {
        if (this.accessoryRead?.controller === controller) this.accessoryRead = null;
      }
    }

    syncAccessoryCart(cart) {
      if (!Array.isArray(cart?.items)) return;
      this.confirmedAccessoryCart = cart;
      this.cards.forEach((card) => {
        const lines = cart.items.filter((item) => String(item.product_id) === card.dataset.productId);
        const line = lines.length === 1 ? lines[0] : null;
        const quantity = card.querySelector('.together-quantity-wrapper');
        const add = card.querySelector('.productcard__item__together__item-add');
        card.dataset.cartKey = line?.key || '';
        quantity.classList.toggle('show', Boolean(line));
        quantity.setAttribute('aria-hidden', String(!line));
        quantity.querySelector('.together-qty').textContent = String(line?.quantity || 0);
        quantity.querySelectorAll('button').forEach((button) => { button.disabled = !line; });
        add.hidden = Boolean(line);
        add.disabled = this.cartPending || add.dataset.available !== 'true';
      });
    }

    reportAccessoryCart(message, error = false) {
      if (!this.cartStatus) return;
      this.cartStatus.textContent = message;
      this.cartStatus.classList.toggle('visually-hidden', !error);
    }

    async onAccessoryCartClick(event) {
      const control = event.target instanceof Element
        ? event.target.closest('.productcard__item__together__item-add, .together-qty-btn') : null;
      if (!control || !this.rail.contains(control) || event.defaultPrevented) return;
      event.preventDefault();
      // Only Cart 2.0 owns writes, confirmation, option sheets and protection.
      event.stopPropagation();
      const adding = control.classList.contains('productcard__item__together__item-add');
      if (control.disabled || !this.cartDrawer || (adding && this.cartPending)) return;
      const card = control.closest('[data-product-id]');
      const keyboard = event.detail === 0;
      const controller = this.abortController;
      if (adding) this.cartPending = true;
      this.cartRevision += 1;
      let feedback = this.pendingAccessoryControls.get(control);
      if (!feedback) {
        const spinner = document.createElement('span');
        spinner.className = 'pdp-accessory-spinner';
        spinner.setAttribute('aria-hidden', 'true');
        control.append(spinner);
        control.setAttribute('aria-busy', 'true');
        feedback = { count: 0, spinner };
        this.pendingAccessoryControls.set(control, feedback);
      }
      feedback.count += 1;
      if (this.confirmedAccessoryCart) this.syncAccessoryCart(this.confirmedAccessoryCart);
      this.reportAccessoryCart('Updating cart…');
      try {
        let result;
        if (adding) {
          result = control.dataset.needsOptions === 'true'
            ? await this.cartDrawer.openAccessoryOptions(control)
            : await this.cartDrawer.prepareProductAdd(control, {});
        } else {
          result = await this.cartDrawer.whenMerchandiseConfirmed(this.cartDrawer.changeQuantity(card.dataset.cartKey,
            control.classList.contains('plus') ? 1 : -1, 'Cart updated.'));
        }
        if (this.abortController !== controller || controller.signal.aborted) return;
        if (result?.ok === false) throw result.error || new Error('The cart could not be updated. Open your cart and try again.');
        if (result?.cart) this.syncAccessoryCart(result.cart);
        this.reportAccessoryCart(result?.cart ? 'Cart updated.' : 'Choose your accessory option.');
      } catch (error) {
        if (this.abortController === controller && !controller.signal.aborted) {
          this.reportAccessoryCart(error?.message || 'The cart could not be updated. Open your cart and try again.', true);
        }
      } finally {
        if (this.abortController === controller && !controller.signal.aborted) {
          if (adding) this.cartPending = false;
          const feedback = this.pendingAccessoryControls.get(control);
          if (feedback && --feedback.count === 0) {
            control.removeAttribute('aria-busy');
            feedback.spinner.remove();
            this.pendingAccessoryControls.delete(control);
          }
          if (this.confirmedAccessoryCart) this.syncAccessoryCart(this.confirmedAccessoryCart);
          // A hidden Add/quantity control must not drop keyboard focus to body.
          if (keyboard && (document.activeElement === control || document.activeElement === document.body)) {
            const accessoryCard = control.closest('article');
            const add = accessoryCard?.querySelector('.productcard__item__together__item-add');
            const target = add?.hidden ? accessoryCard.querySelector('.together-qty-btn.plus') : add;
            if (target && !target.disabled) target.focus({ preventScroll: true });
          }
        }
      }
    }


  }
  customElements.define('ai-pbn-accessories-slider', Accessories);
})();
